import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import axios from "../api/axios";
import loadStatus from "../util/loadStatus";
import { capitalizeFirst } from "../util";

export const getTasks = createAsyncThunk(
  "tasks/getTasks",
  async (reqData, { rejectWithValue, getState }) => {
    try {
      const state = getState().tasks;
      if (state.data.length <= 0 && state.count.getTasks <= 1) {
        const res = await axios.get("/tasks");
        return res.data;
      } else return state.data;
    } catch (error) {
      if (error.response && error.response.data) {
        return rejectWithValue(error.response.data);
      }

      return rejectWithValue(error);
    }
  }
);

export const getStatusTasks = createAsyncThunk(
  "tasks/getStatusTasks",
  async (reqData, { rejectWithValue, getState }) => {
    try {
      const state = getState().tasks;
      if (state.count.getStatusTasks <= 1) {
        const res = await axios.get("/status-tasks/" + reqData);
        return res.data;
      } else return state.data;
    } catch (error) {
      if (error.response && error.response.data) {
        return rejectWithValue(error.response.data);
      }

      return rejectWithValue(error);
    }
  }
);

export const addTask = createAsyncThunk(
  "tasks/addTask",
  async (reqData, { rejectWithValue }) => {
    try {
      const res = await axios.post("/add-task", reqData);
      return res.data;
    } catch (error) {
      if (error.response && error.response.data) {
        return rejectWithValue(error.response.data);
      }

      return rejectWithValue(error);
    }
  }
);

export const addTaskRecord = createAsyncThunk(
  "tasks/addTaskRecord",
  async ({ id, reqData }, { rejectWithValue }) => {
    try {
      const res = await axios.post("/add-record/" + id, reqData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
    } catch (error) {
      if (error.response && error.response.data) {
        return rejectWithValue(error.response.data);
      }

      return rejectWithValue(error);
    }
  }
);

export const addTaskNarrative = createAsyncThunk(
  "tasks/addTaskNarrative",
  async ({ id, reqData }, { rejectWithValue }) => {
    try {
      const res = await axios.post("/add-narative/" + id, reqData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      return res.data;
    } catch (error) {
      if (error.response && error.response.data) {
        return rejectWithValue(error.response.data);
      }

      return rejectWithValue(error);
    }
  }
);

export const editTask = createAsyncThunk(
  "tasks/editTask",
  async ({ id, reqData }, { rejectWithValue }) => {
    try {
    } catch (error) {
      if (error.response && error.response.data) {
        return rejectWithValue(error.response.data);
      }

      return rejectWithValue(error);
    }
  }
);

export const deleteTask = createAsyncThunk(
  "tasks/deleteTask",
  async ({ id, reqData }, { rejectWithValue }) => {
    try {
    } catch (error) {
      if (error.response && error.response.data) {
        return rejectWithValue(error.response.data);
      }

      return rejectWithValue(error);
    }
  }
);

const tasksSlice = createSlice({
  name: "tasks",
  initialState: {
    data: [],
    selected: {},
    loading: false,
    error: null,
    load: {
      getTasks: false,
      getTask: false,
      addTask: false,
      addTaskRecord: false,
      editTask: false,
      deleteTask: false,
      getStatusTasks: false,
    },
    count: { getTasks: 0, getTask: 0, getStatusTasks: 0 },
  },
  reducers: {
    setTasks: (state, action) => {
      state.data = action.payload;
    },
    setSelectedTask: (state, action) => {
      state.selected = action.payload;
    },
    toggleOpen: (state, action) => {
      const task = state.data.find((item) => item.id === action.payload);
      task.isOpen = !task.isOpen;
      state.data[state.data.findIndex((item) => item.id === action.payload)] =
        task;
    },
    unsetTasks: (state) => {
      state.data = [];
      state.selected = {};
    },
  },
  extraReducers: (builder) => {
    builder
      //Get all Tasks
      .addCase(getTasks.pending, (state) => {
        state.load.getTasks = loadStatus.loading;
        state.error = null;
        state.count.getTasks = ++state.count.getTasks;
      })
      .addCase(getTasks.fulfilled, (state, action) => {
        state.load.getTasks = loadStatus.success;
        state.data = action.payload;
        state.count.getTasks = 0;
      })
      .addCase(getTasks.rejected, (state, action) => {
        state.load.getTasks = loadStatus.error;
        state.error = action.payload;
        state.count.getTasks = 0;
      })
      //Get Status Tasks
      .addCase(getStatusTasks.pending, (state) => {
        state.load.getStatusTasks = loadStatus.loading;
        state.error = null;
        state.count.getStatusTasks = ++state.count.getStatusTasks;
      })
      .addCase(getStatusTasks.fulfilled, (state, action) => {
        state.load.getStatusTasks = loadStatus.success;
        let tasks = [...action.payload]
          .slice()
          .filter(
            (item) =>
              item.name !== "verified" &&
              item.name !== "canceled" &&
              item.name !== "completed"
          )
          .map((item) => {
            return {
              id: item.id,
              color: item.color,
              isOpen: loadStatus.loading,
              name: capitalizeFirst(item.name),
              tasks: item.tasks,
            };
          });

        state.data = tasks;
        state.count.getStatusTasks = 0;
      })
      .addCase(getStatusTasks.rejected, (state, action) => {
        state.load.getStatusTasks = loadStatus.error;
        state.error = action.payload;
        state.count.getStatusTasks = 0;
      })
      //Add Task
      .addCase(addTask.pending, (state) => {
        state.load.addTask = loadStatus.loading;
        state.error = null;
      })
      .addCase(addTask.fulfilled, (state, action) => {
        state.load.addTask = loadStatus.success;
        state.data.unshift(action.payload);
      })
      .addCase(addTask.rejected, (state, action) => {
        state.load.addTask = loadStatus.error;
        state.error = action.payload;
      })
      //Add Task Record
      .addCase(addTaskRecord.pending, (state) => {
        state.load.addTaskRecord = loadStatus.loading;
        state.error = null;
      })
      .addCase(addTaskRecord.fulfilled, (state, action) => {
        state.load.addTaskRecord = loadStatus.success;
        // state.data.unshift(action.payload);
      })
      .addCase(addTaskRecord.rejected, (state, action) => {
        state.load.addTaskRecord = loadStatus.error;
        state.error = action.payload;
      })
      //Update Task
      .addCase(editTask.pending, (state) => {
        state.load.editTask = loadStatus.loading;
        state.error = null;
      })
      .addCase(editTask.fulfilled, (state, action) => {
        state.load.editTask = loadStatus.success;
        const index = state.data.findIndex(
          (item) => (item.id = action.payload.id)
        );
        if (index !== -1) {
          state.data[index] = action.payload;
        }
      })
      .addCase(editTask.rejected, (state, action) => {
        state.load.editTask = loadStatus.error;
        state.error = action.payload;
      })
      //Delete Task
      .addCase(deleteTask.pending, (state) => {
        state.load.deleteTask = loadStatus.loading;
        state.error = null;
      })
      .addCase(deleteTask.fulfilled, (state, action) => {
        state.load.deleteTask = loadStatus.success;
        state.data.filter((item) => item.id !== action.meta.arg.id);
      })
      .addCase(deleteTask.rejected, (state, action) => {
        state.load.deleteTask = loadStatus.error;
        state.error = action.payload;
      });
  },
});

export const { setTasks, setSelectedTask, toggleOpen, unsetTasks } =
  tasksSlice.actions;
export default tasksSlice.reducer;
